import { ManualTrade } from './TradeBlotter';

const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    signDisplay: 'exceptZero',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

class TradeCalculator {
    getAdjustedDirtyPrice = (data: ManualTrade) => {
        if (!data.quote) {
            return undefined;
        }

        const cleanPrice = this.getCleanPrice(data)!;
        const dirtyPrice = data.asset?.assetClass === 'Bond' ? cleanPrice + this.getAccruedInterest(data) : cleanPrice;
        return dirtyPrice * (1 + (Number(data.price?.percentage) / 100 || 0));
    };

    getAdjustedCleanPrice = (data: ManualTrade) => {
        if (!data.quote) {
            return undefined;
        }

        const cleanPrice = this.getCleanPrice(data)!;
        return cleanPrice * (1 + (Number(data.price?.percentage) / 100 || 0));
    };

    getCleanPrice = (data: ManualTrade) => {
        if (!data.quote) {
            return undefined;
        }

        if (data.price?.type === 'MANUAL') {
            return Number(data.price.value);
        }

        const ask = Number(data.quote?.ask);
        const bid = Number(data.quote?.bid);

        if (data.price?.type === 'MID') {
            if (ask && bid) {
                return (ask + bid) / 2;
            }
            // if bid or ask is missing, instead of average return the value that is present
            return ask || bid;
        }

        return data.price?.type === 'ASK' ? ask : bid;
    };

    getTradeQuantity = (data: ManualTrade) => {
        const price = this.getAdjustedDirtyPrice(data);
        const multiplier = this.getMultiplier(data);

        if (!price || !multiplier || !data.budget) {
            return undefined;
        }
        return Math.floor(data.budget / price / multiplier);
    };

    getTradeValue = (data: ManualTrade) => {
        const price = this.getAdjustedDirtyPrice(data);
        const multiplier = this.getMultiplier(data);

        if (!price || !multiplier) {
            return undefined;
        }
        return price * multiplier * Number(data.quantity);
    };

    getPriceDeviation = (data: ManualTrade) => {
        if (data.price?.percentage) {
            return `${data.price?.percentage > 0 ? '+' : ''}${data.price?.percentage}%`;
        }

        if (data.price?.type === 'MANUAL' && data.price?.value) {
            const refPrice = this.getAdjustedCleanPrice({
                quote: data.quote,
                price: { type: this.getPriceTypeFromTradeSide(data.side) },
            });
            const priceDiff = refPrice && data.price?.value - refPrice;
            return priceDiff !== undefined ? formatter.format(priceDiff) : null;
        }

        return null;
    };

    getPriceTypeFromTradeSide = (side?: string) => (side === 'BUY' ? 'ASK' : 'BID');

    getMultiplier = (data: ManualTrade) =>
        (data.asset?.contractMultiplier &&
            data.asset?.priceMultiplier &&
            Number(data.asset?.contractMultiplier) * Number(data.asset?.priceMultiplier)) ||
        undefined;

    getAccruedInterest = (data: ManualTrade) =>
        (data.quote && Number(Number(data.quote.accruedInterest ?? 0).toFixed(4))) || 0;
}

export const tradeCalculator = new TradeCalculator();
